/**
 * What an identity row of the History card says (PRD-0075 slice 02, ADR-0091): "Merged with
 * <name> (<reason>, <actor>)", "Merged into <name> (…)" in the absorbed entity's own history,
 * "Merge with <name> undone (<actor>)", "Not the same as <name> (<actor>)", and a mention that
 * arrived under an entity merged into this one, "Mentioned in <document> as <alias>".
 */
import type { TFunction } from 'i18next'
import type { CaseEventKind } from '@/services/api/case-events'

const IDENTITY = 'investigations.entityDetail.history.identity'

/** Why two entities became one: `payload.reason` of `entities_merged` (`MergeReason` on the server). */
export const MERGE_REASONS = [
  'schema_key',
  'fuzzy_person',
  'geocoded_address',
  'analyst',
  'face',
  'voice',
  'tattoo',
] as const

export type MergeReason = (typeof MERGE_REASONS)[number]

/** Who made the change: the system, or a user by name (null when the case does not list them). */
export type HistoryActor = { kind: 'system' } | { kind: 'user'; name: string | null }

/** A merge, its undo or a dismissed match, as this entity's history sees it. */
export interface IdentityChange {
  /** The other entity's name when the change was made; null when it had none. */
  otherName: string | null
  /** This entity is the one a merge absorbed (its own history after an undo). */
  absorbed: boolean
  /** Why the two became one; null for an undo, a dismissal or a reason this build does not know. */
  reason: MergeReason | null
  actor: HistoryActor
}

/** What the text of an identity row needs. */
export interface IdentityRowText {
  kind: CaseEventKind | null
  documentName: string | null
  /** The name of the entity a mention arrived under, when that was one merged into this one. */
  alias?: string | null
  identity?: IdentityChange | null
}

export function historyActorText(actor: HistoryActor, t: TFunction): string {
  if (actor.kind === 'system') return t(`${IDENTITY}.actors.system`)
  return actor.name ?? t(`${IDENTITY}.actors.unknownUser`)
}

/** The row's wording; null for a row that is no identity change and no mention under an alias. */
export function identityRowText(row: IdentityRowText, t: TFunction): string | null {
  if (row.kind === 'entity_mentioned') {
    if (!row.alias) return null
    const document = row.documentName ?? t('investigations.entityDetail.unknownDocument')
    return t(`${IDENTITY}.mentionedAs`, { document, alias: row.alias })
  }
  const change = row.identity
  if (!change) return null
  const values = {
    name: change.otherName ?? t(`${IDENTITY}.unnamed`),
    actor: historyActorText(change.actor, t),
  }
  switch (row.kind) {
    case 'entities_merged':
      return t(`${IDENTITY}.${change.absorbed ? 'mergedInto' : 'mergedWith'}`, {
        ...values,
        reason: t(`${IDENTITY}.reasons.${change.reason ?? 'other'}`),
      })
    case 'merge_reverted':
      return t(`${IDENTITY}.mergeUndone`, values)
    case 'pair_dismissed':
      return t(`${IDENTITY}.dismissed`, values)
    default:
      return null
  }
}
